import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { REGISTER_PLACEHOLDER } from '@assetaccountant/core/http-token.interceptor';
import { AssetAddFirstUseRequest } from '@assetaccountant/core/models/asset-add-first-use-request';
import { AssetAddPurchaseRequest } from '@assetaccountant/core/models/asset-add-purchase-request';
import { ComponentsSearchResponse } from '@assetaccountant/core/models/components-search-result';
import { ISearchResponse } from '@assetaccountant/core/models/search-response';
import { SellQuantityCommand } from '@assetaccountant/core/models/sell-quantity-command';
import { SetQuantityCommand } from '@assetaccountant/core/models/set-quantity-command';
import { WriteOffQuantityCommand } from '@assetaccountant/core/models/write-off-quantity-command';
import { SearchAssetsCache } from '@assetaccountant/core/services/search-assets.cache';
import { Observable } from 'rxjs';

import { environment } from '../../../../environments/environment';
import { ISearchQuery } from '../../core/models/search-query';
import { ISearchService } from '../../core/services/search.service';
import { AddRevaluationCommand } from '../models/add-revaluation-command';
import { AddUsageCommand } from '../models/add-usage-command';
import { AssetDisposal } from '../models/asset-disposal';
import {
  TransferAssetToPoolAlertsRequestApiModel,
  TransferAssetToPoolAlertsResponseApiModel,
  TransferAssetToPoolApiModel,
} from '../models/asset-transfer-to-pool';
import { CreateAssetRequest } from '../models/create-asset-request';
import { EditAssetFieldCommand } from '../models/edit-asset-field-command';
import { FieldAssignmentDto } from '../models/field-assignment-dto';
import { SaveAssetResponse } from '../models/save-asset-response';
import { SaveTaxableUseRequest } from '../models/save-taxable-use-request';
import { SaveTaxableUseResponse } from '../models/save-taxable-use-response';
import { UpdateAssetRequest } from '../models/update-asset-request';

export const MAXIMUM_DEMO_ASSETS = 100;

@Injectable({
  providedIn: 'root',
})
export class AssetsService implements ISearchService {
  constructor(
    private http: HttpClient,
    private searchAssetsCache: SearchAssetsCache,
  ) {}

  transferToPool(model: TransferAssetToPoolApiModel): Observable<any> {
    this.searchAssetsCache.invalidate(model.assetId);

    return this.http.post<any>(`${environment.servicesApiUrl}/api/asset/transfer-to-pool`, model);
  }

  transferToPoolAlerts(
    model: TransferAssetToPoolAlertsRequestApiModel,
  ): Observable<TransferAssetToPoolAlertsResponseApiModel> {
    return this.http.post<any>(
      `${environment.servicesApiUrl}/api/asset/transfer-to-pool/alerts`,
      model,
    );
  }

  search(query: ISearchQuery): Observable<ISearchResponse> {
    const searchUrl = `${environment.servicesApiUrl}/api/v2/registers/${REGISTER_PLACEHOLDER}/assets/search`;

    return this.http.get<ISearchResponse>(searchUrl, {
      params: {
        searchCriteria: query.searchCriteria,
        numberOfResults: query.numberOfResults.toString(),
      },
    });
  }

  create(asset: CreateAssetRequest): Observable<any> {
    return this.http.post(`${environment.servicesApiUrl}/api/asset`, asset);
  }

  save(assetId: number, request: UpdateAssetRequest): Observable<SaveAssetResponse> {
    this.searchAssetsCache.invalidate(assetId);

    return this.http.put<SaveAssetResponse>(
      `${environment.servicesApiUrl}/api/asset/${assetId}`,
      request,
    );
  }

  addFirstUse(request: AssetAddFirstUseRequest): Observable<any> {
    this.searchAssetsCache.invalidate(request.assetId);

    return this.http.put(
      `${environment.servicesApiUrl}/api/asset/${request.assetId}/firstUse`,
      request,
    );
  }

  addPurchase(request: AssetAddPurchaseRequest): Observable<any> {
    this.searchAssetsCache.invalidate(request.assetId);

    return this.http.put(
      `${environment.servicesApiUrl}/api/asset/${request.assetId}/purchase`,
      request,
    );
  }

  saveTaxableUse(assetId: number, request: SaveTaxableUseRequest) {
    this.searchAssetsCache.invalidate(assetId);

    return this.http.post<SaveTaxableUseResponse[]>(
      `${environment.servicesApiUrl}/api/asset/${assetId}/taxable-use`,
      request,
    );
  }

  writeOff(assetDisposal: AssetDisposal): Observable<any> {
    this.searchAssetsCache.invalidate(assetDisposal.assetId);

    return this.http.post(`${environment.servicesApiUrl}/api/asset/write-off`, assetDisposal);
  }

  sell(assetDisposal: AssetDisposal): Observable<any> {
    this.searchAssetsCache.invalidate(assetDisposal.assetId);

    return this.http.post(`${environment.servicesApiUrl}/api/asset/sell`, assetDisposal);
  }

  setQuantity(assetId: number, assetQuantity: SetQuantityCommand): Observable<any> {
    this.searchAssetsCache.invalidate(assetId);

    return this.http.post(
      `${environment.servicesApiUrl}/api/asset/${assetId}/quantity`,
      assetQuantity,
    );
  }

  writeOffQuantity(assetId: number, assetDisposal: WriteOffQuantityCommand): Observable<any> {
    this.searchAssetsCache.invalidate(assetId);

    return this.http.post(
      `${environment.servicesApiUrl}/api/asset/${assetId}/write-off-quantity`,
      assetDisposal,
    );
  }

  sellQuantity(assetId: number, assetDisposal: SellQuantityCommand): Observable<any> {
    this.searchAssetsCache.invalidate(assetId);

    return this.http.post(
      `${environment.servicesApiUrl}/api/asset/${assetId}/sell-quantity`,
      assetDisposal,
    );
  }

  delete(assetId: number): Observable<any> {
    this.searchAssetsCache.invalidate(assetId);

    return this.http.delete(`${environment.servicesApiUrl}/api/asset/${assetId}`);
  }

  forceDelete(assetId: number): Observable<any> {
    this.searchAssetsCache.invalidate(assetId);

    return this.http.delete(`${environment.servicesApiUrl}/api/asset/${assetId}/force`);
  }

  addOpeningBalanceTransaction(
    assetId: number,
    openingBalanceDate: string,
    regimes: Array<{
      regimeId: number;
      writtenDownValue: number;
    }>,
  ): Observable<any> {
    this.searchAssetsCache.invalidate(assetId);

    return this.http.post(`${environment.servicesApiUrl}/api/asset/opening-balance`, {
      assetId,
      openingBalanceDate,
      regimes,
    });
  }

  addReassessment(
    assetId: number,
    request: {
      effectiveDate: string;
      regimeId: number;
      depreciationMethodId: number;
      life: number | null;
      rate: number | null;
      selfAssessed: boolean | null;
    },
  ): Observable<any> {
    this.searchAssetsCache.invalidate(assetId);

    return this.http.post(
      `${environment.servicesApiUrl}/api/asset/${assetId}/reassessment`,
      request,
    );
  }

  addRevaluation(request: AddRevaluationCommand): Observable<any> {
    this.searchAssetsCache.invalidate(request.assetId);

    return this.http.post(`${environment.servicesApiUrl}/api/asset/revaluation`, request);
  }

  addUsage(request: AddUsageCommand): Observable<any> {
    this.searchAssetsCache.invalidate(request.assetId);

    return this.http.post(`${environment.servicesApiUrl}/api/asset/usage`, request);
  }

  addFields(assetId: number, date: string, fields: FieldAssignmentDto[]): Observable<any> {
    this.searchAssetsCache.invalidate(assetId);

    return this.http.post(`${environment.servicesApiUrl}/api/asset/${assetId}/fields`, {
      date,
      fields,
    });
  }

  export(assetId: number) {
    return this.http.get(`${environment.servicesApiUrl}/api/asset/${assetId}/export`);
  }

  updateField(assetId: number, command: EditAssetFieldCommand) {
    return this.http.put(`${environment.servicesApiUrl}/api/asset/${assetId}/field`, command);
  }

  addComponent(assetId: number, componentId: number): Observable<any> {
    return this.http.put(`${environment.servicesApiUrl}/api/asset/${assetId}/component`, {
      componentId,
    });
  }

  removeComponent(assetId: number, componentId: number): Observable<any> {
    return this.http.request(
      'delete',
      `${environment.servicesApiUrl}/api/asset/${assetId}/component`,
      {
        body: { componentId },
      },
    );
  }

  searchComponents(assetId: number, query: ISearchQuery): Observable<ComponentsSearchResponse> {
    const searchUrl = `${environment.servicesApiUrl}/api/asset/${assetId}/components`;

    return this.http.get<ComponentsSearchResponse>(searchUrl, {
      params: {
        searchCriteria: query.searchCriteria,
        numberOfResults: query.numberOfResults.toString(),
      },
    });
  }
}
